import clsx from 'clsx';

import styles from './icons.module.css';

/**
 * The icon set the primitives need, drawn inline on a 16 unit grid.
 *
 * Inline rather than an icon package: every glyph below is geometric, the
 * package has no runtime dependency budget, and an icon that ships as data
 * cannot fail to load. Size comes from CSS (`--c-icon-16` / `--c-icon-20`), so
 * no SVG here carries a width attribute.
 *
 * Every icon is `aria-hidden`: it is always accompanied by text or by a
 * VisuallyHidden label. An icon is never the only channel.
 */

export interface IconProps {
  /** `md` = --c-icon-16, `lg` = --c-icon-20. */
  size?: 'md' | 'lg';
  className?: string;
}

type Glyph = { readonly path: string; readonly title: string };

const glyph =
  ({ path, title }: Glyph) =>
  function Icon({ size = 'md', className }: IconProps) {
    return (
      <svg
        viewBox="0 0 16 16"
        focusable="false"
        aria-hidden="true"
        data-icon={title}
        className={clsx(styles.icon, size === 'lg' && styles.lg, className)}
      >
        <path d={path} />
      </svg>
    );
  };

export const CheckIcon = glyph({
  title: 'check',
  path: 'M6.3 11.3 3 8l-1 1 4.3 4.3L14 5.6l-1-1z',
});

export const CloseIcon = glyph({
  title: 'close',
  path: 'M12 4.7 11.3 4 8 7.3 4.7 4 4 4.7 7.3 8 4 11.3l.7.7L8 8.7l3.3 3.3.7-.7L8.7 8z',
});

export const WarningIcon = glyph({
  title: 'warning',
  path: 'M8 1 .8 14.5h14.4zM7.4 5.4h1.2v5.1H7.4zm0 6.1h1.2v1.4H7.4z',
});

export const InfoIcon = glyph({
  title: 'info',
  path: 'M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1m.6 11.2H7.4V6.9h1.2zm0-6.4H7.4V3.6h1.2z',
});

export const ErrorIcon = glyph({
  title: 'error',
  path: 'M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1m2.7 8.9-.8.8L8 8.8l-1.9 1.9-.8-.8L7.2 8 5.3 6.1l.8-.8L8 7.2l1.9-1.9.8.8L8.8 8z',
});

export const SuccessIcon = glyph({
  title: 'success',
  path: 'M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1m-.9 10.2L4.4 8.5l.8-.8 1.9 1.9 3.7-3.7.8.8z',
});

export const ChevronDownIcon = glyph({
  title: 'chevron-down',
  path: 'M8 11 3 6l.9-.9L8 9.2l4.1-4.1L13 6z',
});

export const ChevronUpIcon = glyph({
  title: 'chevron-up',
  path: 'M8 5l5 5-.9.9L8 6.8l-4.1 4.1L3 10z',
});

export const ChevronRightIcon = glyph({
  title: 'chevron-right',
  path: 'M6 13.1 5.1 12.2 9.3 8 5.1 3.8 6 2.9 11.1 8z',
});

export const ChevronLeftIcon = glyph({
  title: 'chevron-left',
  path: 'M10 2.9l.9.9L6.7 8l4.2 4.2-.9.9L4.9 8z',
});

export const SearchIcon = glyph({
  title: 'search',
  path: 'M14.7 13.8 11.2 10.3a5.2 5.2 0 1 0-.9.9l3.5 3.5zM7 11.2A4.2 4.2 0 1 1 7 2.8a4.2 4.2 0 0 1 0 8.4',
});

export const ExternalIcon = glyph({
  title: 'external',
  path: 'M13 13H3V3h4V2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V9h-1zM9 2v1h3.3L7.6 7.7l.7.7L13 3.7V7h1V2z',
});

export const SortIcon = glyph({
  title: 'sort',
  path: 'M8 2.4 4.7 6.2h6.6zM8 13.6l3.3-3.8H4.7z',
});

export const SortAscIcon = glyph({
  title: 'sort-ascending',
  path: 'M8 2.4 4 7h8z',
});

export const SortDescIcon = glyph({
  title: 'sort-descending',
  path: 'M8 13.6 4 9h8z',
});
